import { type FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import {
  useCreateGroupApi,
  useGroupApi,
  useUpdateGroupApi,
} from "../../../../hooks/use-distribution-api";

interface GroupFormData {
  name: string;
  description: string;
  isDefault: boolean;
}

export const GroupFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existingGroup, loading: loadingGroup } = useGroupApi({
    id: id || "",
    skip: !id,
  });
  const {
    createGroup,
    loading: creating,
    error: createError,
  } = useCreateGroupApi();
  const {
    updateGroup,
    loading: updating,
    error: updateError,
  } = useUpdateGroupApi();

  const [formData, setFormData] = useState<GroupFormData>({
    name: "",
    description: "",
    isDefault: false,
  });
  const [errors, setErrors] = useState<Partial<GroupFormData>>({});

  useEffect(() => {
    if (existingGroup) {
      setFormData({
        name: existingGroup.name,
        description: existingGroup.description || "",
        isDefault: existingGroup.isDefault,
      });
    }
  }, [existingGroup]);

  const validate = (): boolean => {
    const newErrors: Partial<GroupFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = "グループ名は必須です";
    } else if (formData.name.length > 255) {
      newErrors.name = "グループ名は255文字以内で入力してください";
    }

    if (formData.description.length > 500) {
      newErrors.description = "説明は500文字以内で入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      if (isEdit && id) {
        await updateGroup(id, {
          name: formData.name,
          description: formData.description || undefined,
          isDefault: formData.isDefault,
        });
        navigate(`/admin/distribution/groups/${id}`);
      } else {
        const created = await createGroup({
          name: formData.name,
          description: formData.description || undefined,
          isDefault: formData.isDefault,
        });
        navigate(`/admin/distribution/groups/${created.id}`);
      }
    } catch (_e) {
      // Error is handled by the hook
    }
  };

  const apiError = createError || updateError;
  const isLoading = creating || updating;

  if (isEdit && loadingGroup) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>グループ編集</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/distribution/groups" className="btn btn-ghost btn-sm">
          ← 戻る
        </Link>
        <PageTitle>{isEdit ? "グループ編集" : "グループ作成"}</PageTitle>
      </div>

      <div className="card bg-base-100 shadow-md max-w-2xl">
        <div className="card-body">
          {apiError && (
            <div className="alert alert-error mb-4">
              <span>{apiError.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* グループ名 */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">
                  グループ名 <span className="text-error">*</span>
                </span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e =>
                  setFormData(prev => ({ ...prev, name: e.target.value }))
                }
                className={`input input-bordered ${errors.name ? "input-error" : ""}`}
                placeholder="営業部A"
              />
              {errors.name && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.name}
                  </span>
                </label>
              )}
            </div>

            {/* 説明 */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">説明</span>
              </label>
              <textarea
                value={formData.description}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className={`textarea textarea-bordered ${
                  errors.description ? "textarea-error" : ""
                }`}
                placeholder="営業部Aの配信先リスト"
                rows={3}
              />
              {errors.description && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.description}
                  </span>
                </label>
              )}
            </div>

            {/* デフォルトグループ */}
            <div className="form-control mb-6">
              <label className="label cursor-pointer justify-start gap-4">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      isDefault: e.target.checked,
                    }))
                  }
                  className="checkbox checkbox-primary"
                />
                <div>
                  <span className="label-text">
                    デフォルトグループに設定する
                  </span>
                  <div className="text-sm text-base-content/60">
                    配信作成時に自動選択されます
                  </div>
                </div>
              </label>
            </div>

            {/* ボタン */}
            <div className="flex justify-end gap-2">
              <Link to="/admin/distribution/groups" className="btn btn-ghost">
                キャンセル
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : isEdit ? (
                  "更新"
                ) : (
                  "作成"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
